import { datadogRum } from "@datadog/browser-rum";
import {
  Table,
  TableBody,
  TableRow as MuiTableRow,
  TableCell,
  Typography,
  TypographyProps,
  Box,
  Alert,
  Divider,
} from "@mui/material";
import { keyBy } from "lodash";
import { ReactElement, ReactNode } from "react";

import { EvaluationCriteriaSchema } from "@stw/yup-validators";

import {
  RoiSingleVariantResult,
  CampaignPartialSendEvaluationStartedEvent,
  CampaignPartialSendEvaluationPassedEvent,
  PartialSendEvaluationCriteriaType,
  CampaignPartialSendEvaluationFailedEvent,
  CampaignPartialSendEvaluationCanceledEvent,
} from "~frontend/__generated__/graphql";
import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { criteriaTypeToLabel } from "~frontend/routes/subaccounts/campaignView/initialSend/constants";
import { CampaignForView } from "~frontend/routes/subaccounts/campaignView/types";
import { palette } from "~frontend/theme";
import {
  formatDateTime,
  formatDate,
  formatTime,
  timezoneDisplayName,
} from "~frontend/util/dateFormat";
import { plural } from "~frontend/util/plural";

type VariantResultData = Omit<RoiSingleVariantResult, "__typename">;

type PartialSendEvent =
  | CampaignPartialSendEvaluationStartedEvent
  | CampaignPartialSendEvaluationPassedEvent
  | CampaignPartialSendEvaluationFailedEvent
  | CampaignPartialSendEvaluationCanceledEvent;

type PartialSendEvalForView = CampaignForView["partialSendEvaluations"][0];

type ScriptVariantsById = ReturnType<
  typeof keyBy<CampaignForView["scriptVariants"][0] & { displayOrder: number }>
>;

function toUsdDisplay(amountInCents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amountInCents / 100);
}

function isPartialSendEvent(
  event: CampaignForView["events"][0]
): event is PartialSendEvent {
  return (
    event.__typename === "CampaignPartialSendEvaluationStartedEvent" ||
    event.__typename === "CampaignPartialSendEvaluationPassedEvent" ||
    event.__typename === "CampaignPartialSendEvaluationFailedEvent" ||
    event.__typename === "CampaignPartialSendEvaluationCanceledEvent"
  );
}

export default function PartialSendEvaluationResults({
  campaign,
}: {
  campaign: CampaignForView;
}): ReactElement {
  const subaccount = useSubaccountAdminConfig()!;
  const timezone =
    campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone;
  const partialSendEvents = campaign.events.filter(isPartialSendEvent);
  return (
    <FlexBox gap={4}>
      <Box sx={{ width: "100%" }}>
        {campaign.partialSendEvaluations.map(pse => (
          <EvaluationInputDisplay
            partialEval={pse}
            timezone={timezone}
            key={pse.id}
          />
        ))}
      </Box>

      <FlexBox sx={{ width: "100%" }} direction="column">
        <EventDisplay events={partialSendEvents} campaign={campaign} />
      </FlexBox>
    </FlexBox>
  );
}

function EvaluationInputDisplay({
  partialEval,
  timezone,
}: {
  partialEval: PartialSendEvalForView;
  timezone: string;
}): ReactElement {
  const criteria = EvaluationCriteriaSchema.parse(partialEval.criteria);
  const autoSendBasedOn = criteriaTypeToLabel[criteria.type];
  const { batchSize, startsAt, completedAt } = partialEval;
  return (
    <FlexBox
      direction="column"
      sx={{ backgroundColor: palette.lightestGray, padding: 3, mb: 2 }}
    >
      <Typography variant="body1" sx={{ mb: 1, ml: 2 }}>
        Manual Partial Send {partialEval.series + 1} Parameters
      </Typography>
      <Table size="small">
        <TableBody>
          <TableRow label="Texts to Send" value={batchSize} />
          {startsAt && (
            <TableRow label="Send Date" value={formatDateTime(startsAt)} />
          )}
          {autoSendBasedOn && (
            <TableRow label="Autosend Based On" value={autoSendBasedOn} />
          )}
          <TableRow
            label="Minimum ROI"
            value={
              criteria.minRoi
                ? String(
                    Math.floor(parseFloat(String(criteria.minRoi)) * 100)
                  ) + "%"
                : "N/A"
            }
          />
          <TableRow
            label="Minimum Number of Donations"
            value={criteria.minNumberOfDonations || "N/A"}
          />
          {!!criteria.excludeDonationsOverInCents && (
            <TableRow
              label="Exclude Donations Over"
              value={toUsdDisplay(criteria.excludeDonationsOverInCents)}
            />
          )}
          {completedAt && (
            <>
              <TableRow
                label="Date to Pick a Winner"
                value={formatDate(completedAt)}
              />
              <TableRow
                label="Time to Pick a Winner"
                value={`${formatTime(completedAt)} ${timezoneDisplayName(timezone)}`}
              />
            </>
          )}
          {criteria.autosendOption && (
            <TableRow
              label="Autosend Option"
              value={
                criteria.autosendOption.type === "SEND_TO_FULL"
                  ? "send the winner to the remainder of the list"
                  : "send the winner to a portion of the list, then pause"
              }
            />
          )}
        </TableBody>
      </Table>
    </FlexBox>
  );
}

function EventDisplay({
  events,
  campaign,
}: {
  events: PartialSendEvent[];
  campaign: CampaignForView;
}): ReactElement {
  // Variants are 0-indexed in the DB; bump to 1-indexed for display. Defaults
  // null (variants deleted via campaign edit) to 1.
  const scriptVariants = campaign.scriptVariants.map(v => ({
    ...v,
    displayOrder: (v.order ?? 0) + 1,
  }));
  const scriptVariantsById = keyBy(scriptVariants, c => c.id);
  return (
    <>
      {events.map((e, i) => {
        let eventResult: ReactNode = null;
        switch (e.__typename) {
          case "CampaignPartialSendEvaluationPassedEvent":
          case "CampaignPartialSendEvaluationFailedEvent": {
            const pse = campaign.partialSendEvaluations.find(
              p => p.id === e.data.partialSendEvaluationId
            )!;
            const sharedProps = {
              event: e,
              partialSendEval: pse,
              scriptVariantsById,
            };
            eventResult =
              pse.criteria.type ===
              PartialSendEvaluationCriteriaType.VariantReachedMinRoi ? (
                <SingleVariantSendComplete {...sharedProps} />
              ) : (
                <MultiVariantSendComplete {...sharedProps} />
              );
            break;
          }
          case "CampaignPartialSendEvaluationStartedEvent":
            eventResult = (
              <SendStarted event={e} scriptVariantsById={scriptVariantsById} />
            );
            break;
          case "CampaignPartialSendEvaluationCanceledEvent": {
            const pse = campaign.partialSendEvaluations.find(
              p => p.id === e.data.partialSendEvaluationId
            )!;
            eventResult = (
              <SendCanceled
                event={e}
                partialSendEval={pse}
                scriptVariantsById={scriptVariantsById}
              />
            );
            break;
          }
          default:
            // TypeScript exhaustiveness check not possible here: generated GraphQL
            // types have optional __typename and non-literal type fields, so neither
            // can serve as a discriminant to narrow the union to never.
            datadogRum.addError(
              new Error(
                `Unhandled PartialSendEvent __typename: ${e.__typename}`
              )
            );
            break;
        }
        return (
          <Box key={e.id} sx={{ mb: 1 }}>
            {i !== 0 && (
              <Divider sx={{ mb: 1, borderBottomColor: palette.midGray }} />
            )}
            {eventResult}
          </Box>
        );
      })}
    </>
  );
}

function SendStarted({
  event,
  scriptVariantsById,
}: {
  event: CampaignPartialSendEvaluationStartedEvent;
  scriptVariantsById: ScriptVariantsById;
}): ReactElement {
  return (
    <Box>
      <Typography variant="body2Bold">
        {formatDateTime(event.createdAt)}
      </Typography>
      {event.data.variantSendCounts.map(v => (
        <TypographyBody2 key={v.variantId}>
          Variant {scriptVariantsById[v.variantId].displayOrder} sent to{" "}
          {plural(v.count, "contact", "contacts")}
        </TypographyBody2>
      ))}
    </Box>
  );
}

function SendCanceled({
  event,
  partialSendEval,
  scriptVariantsById,
}: {
  event: CampaignPartialSendEvaluationCanceledEvent;
  partialSendEval: PartialSendEvalForView;
  scriptVariantsById: ScriptVariantsById;
}): ReactElement {
  const variantOrders = partialSendEval.criteria.variantIds
    .map(id => scriptVariantsById[id]?.displayOrder)
    .filter((o): o is number => o != null)
    .join(", ");
  return (
    <Box>
      <Typography variant="body2Bold">
        {formatDateTime(event.createdAt)}
      </Typography>
      <TypographyBody2>
        Evaluation canceled for variants: Variant {variantOrders}
      </TypographyBody2>
    </Box>
  );
}

function MultiVariantSendComplete({
  event,
  partialSendEval,
  scriptVariantsById,
}: {
  event:
    | CampaignPartialSendEvaluationPassedEvent
    | CampaignPartialSendEvaluationFailedEvent;
  partialSendEval: PartialSendEvalForView;
  scriptVariantsById: ScriptVariantsById;
}): ReactElement | null {
  const result =
    partialSendEval.result?.__typename === "RoiMultiVariantEvaluationResult"
      ? partialSendEval.result
      : null;
  if (!result) {
    return null;
  }
  const { excludeDonationsOverInCents } = partialSendEval.criteria;
  const { winningVariantId } = result;

  // Place the winning variant first so it appears at the top of the list
  const winner = result.variantResults.find(
    vr => vr.variantId === winningVariantId
  );
  const losers = result.variantResults.filter(
    vr => vr.variantId !== winningVariantId
  );
  const orderedVariantResults = winner ? [winner, ...losers] : losers;

  return (
    <>
      <Typography variant="body2Bold" sx={{ mb: 2 }}>
        {formatDateTime(event.createdAt)}
      </Typography>
      {winningVariantId && (
        <TypographyBody2 sx={{ mb: 2 }}>
          Variant {scriptVariantsById[winningVariantId].displayOrder} picked as
          winner
        </TypographyBody2>
      )}
      {!winningVariantId && (
        <TypographyBody2 sx={{ mb: 2 }}>
          No winner found, sending paused
        </TypographyBody2>
      )}
      {orderedVariantResults.map(vr => (
        <Box key={vr.variantId} sx={{ mb: 1 }}>
          <VariantResult
            title={
              <FlexBox alignItems="center" gap={1}>
                {winningVariantId && (
                  <VariantBadge
                    status={
                      vr.variantId === winningVariantId ? "winner" : "loser"
                    }
                  />
                )}

                {/*We use vr.variantOrder here because the backend clears the order once a variant is deleted*/}
                <TypographyBody2>{`VARIANT ${vr.variantOrder! + 1} RESULTS`}</TypographyBody2>
              </FlexBox>
            }
            excludeDonationsOverInCents={excludeDonationsOverInCents}
            result={vr}
          />
        </Box>
      ))}
    </>
  );
}

function SingleVariantSendComplete({
  event,
  partialSendEval,
}: {
  event:
    | CampaignPartialSendEvaluationPassedEvent
    | CampaignPartialSendEvaluationFailedEvent;
  partialSendEval: PartialSendEvalForView;
}): ReactElement | null {
  const result =
    partialSendEval.result?.__typename === "RoiSingleVariantEvaluationResult"
      ? partialSendEval.result
      : null;
  if (!result) {
    return null;
  }
  const success = partialSendEval.state === "SUCCESS";
  const { variantOrder } = result;
  return (
    <Box>
      <Typography variant="body2Bold" sx={{ mb: 2 }}>
        {formatDateTime(event.createdAt)}
      </Typography>
      <TypographyBody2 sx={{ mb: 2 }}>
        {/*We use vr.variantOrder here because the backend clears the order once a variant is deleted*/}
        Variant {variantOrder! + 1} evaluation{" "}
        {success ? "succeeded" : "failed"}
      </TypographyBody2>
      <VariantResult
        title={`VARIANT ${variantOrder} RESULTS`}
        result={result}
        excludeDonationsOverInCents={
          partialSendEval.criteria.excludeDonationsOverInCents
        }
      />
    </Box>
  );
}

function VariantResult({
  result,
  title,
  excludeDonationsOverInCents,
}: {
  result: VariantResultData;
  title: ReactNode | string;
  excludeDonationsOverInCents?: number | null;
}): ReactElement {
  const {
    roi,
    donationCount,
    totalSent,
    totalRaisedInCents,
    filterCount,
    replyCount,
    optOutCount,
    clickCount,
    excludedDonations,
  } = result;

  const stats = [
    {
      label: "Total Raised",
      value: toUsdDisplay(totalRaisedInCents),
    },
    { label: "Number of Donations", value: donationCount },
    { label: "Estimated ROI", value: `${Math.floor(roi)}%` },
  ];
  const rates = [
    { label: "Click Rate", numerator: clickCount },
    { label: "Reply Rate", numerator: replyCount },
    { label: "Filter Rate", numerator: filterCount },
    { label: "Opt Out Rate", numerator: optOutCount },
  ];
  return (
    <CollapsibleDrawer
      title={title}
      startOpen={true}
      sxHeader={{
        borderTopStyle: "none",
        borderBottomColor: palette.midGray,
        borderBottomWidth: 1,
        borderBottomStyle: "solid",
        mb: 1,
        mx: 0,
        px: 0,
        pb: 0,
      }}
    >
      {stats.map(s => (
        <FlexMargin key={s.label}>
          <TypographyBody2>{s.label}</TypographyBody2>
          <TypographyBody2>{s.value}</TypographyBody2>
        </FlexMargin>
      ))}
      {rates.map(r => (
        <FlexMargin key={r.label}>
          <PercentageRow
            label={r.label}
            numerator={r.numerator}
            denominator={totalSent}
          />
        </FlexMargin>
      ))}
      {excludeDonationsOverInCents && excludedDonations.length > 0 && (
        <Alert severity="warning" icon={false} sx={{ pb: 2 }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2Bold" component="span">
              Omitted from results:{" "}
            </Typography>
            <TypographyBody2 component="span">
              {excludedDonations.length} over{" "}
              {toUsdDisplay(excludeDonationsOverInCents)}
            </TypographyBody2>
          </Box>
          {excludedDonations.map((d, i) => (
            <TypographyBody2 key={i} sx={{ ml: 2 }}>
              1 Donation of {toUsdDisplay(d)}
            </TypographyBody2>
          ))}
        </Alert>
      )}
    </CollapsibleDrawer>
  );
}

function VariantBadge({
  status,
}: {
  status: "winner" | "loser";
}): ReactElement {
  const isWinner = status === "winner";
  return (
    <Alert
      icon={false}
      severity={isWinner ? "success" : "warning"}
      sx={{ py: 0, px: 1 }}
    >
      <Typography variant="body2Bold" component="span">
        {isWinner ? "Winner" : "Loser"}
      </Typography>
    </Alert>
  );
}

function TableRow({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}): ReactElement {
  return (
    <MuiTableRow>
      <TableCell>
        <Typography variant="body2Bold">{label}</Typography>
      </TableCell>
      <TableCell>{value}</TableCell>
    </MuiTableRow>
  );
}

function PercentageRow({
  label,
  numerator,
  denominator,
}: {
  label: string;
  numerator: number;
  denominator: number;
}): ReactElement {
  const percentage = Math.floor((100 * numerator) / denominator);
  return (
    <>
      <TypographyBody2>{label}</TypographyBody2>
      <TypographyBody2>{percentage}%</TypographyBody2>
    </>
  );
}

function TypographyBody2({
  children,
  ...props
}: TypographyProps): ReactElement {
  return (
    <Typography variant="body2" {...props}>
      {children}
    </Typography>
  );
}

function FlexMargin({ children }: { children: ReactNode }): ReactElement {
  return (
    <FlexBox justifyContent="space-between" sx={{ mb: 0.25 }}>
      {children}
    </FlexBox>
  );
}
